<template>
    <div class="advanced-stats-table-wrap">
        <table v-if="rows.length" class="advanced-stats-table">
            <thead>
                <tr class="advanced-stats-table__group-row">
                    <th class="col-player sticky-col" rowspan="2">Игрок</th>
                    <th
                        class="col-played col-played--matches sticky-col sticky-col--matches col-sortable"
                        :class="sortClass(null, 'matches_played')"
                        rowspan="2"
                        title="Матчи, в которых у игрока есть хотя бы одно действие"
                        @click="handleSort(null, 'matches_played')"
                    >
                        Игры
                    </th>
                    <th
                        class="col-played col-played--sets sticky-col sticky-col--sets col-sortable"
                        :class="sortClass(null, 'sets_played')"
                        rowspan="2"
                        title="Партии, в которых у игрока есть хотя бы одно действие"
                        @click="handleSort(null, 'sets_played')"
                    >
                        Партии
                    </th>
                    <th
                        v-for="skill in skills"
                        :key="`group-${skill}`"
                        class="col-skill-group"
                        :colspan="columnsPerSkill"
                    >
                        {{ skillLabels[skill] }}
                    </th>
                </tr>
                <tr class="advanced-stats-table__sub-row">
                    <template v-for="skill in skills" :key="`sub-${skill}`">
                        <th
                            class="col-metric col-sortable"
                            :class="sortClass(skill, 'total')"
                            @click="handleSort(skill, 'total')"
                        >
                            Σ
                        </th>
                        <th
                            class="col-metric col-sortable"
                            :class="sortClass(skill, 'efficiency')"
                            @click="handleSort(skill, 'efficiency')"
                        >
                            %
                        </th>
                        <th
                            v-for="symbol in qualitySymbols"
                            :key="`${skill}-${symbol}`"
                            class="col-metric col-quality"
                            :class="qualityClass(symbol)"
                            :title="qualityTitles[skill]?.[symbol] || symbol"
                        >
                            <div class="quality-header-cell">
                                <span
                                    class="quality-header-cell__symbol col-sortable"
                                    :class="sortClass(skill, symbol, 'count')"
                                    @click="handleSort(skill, symbol, 'count')"
                                >{{ symbol }}</span>
                                <span
                                    class="quality-header-cell__pct col-sortable"
                                    :class="sortClass(skill, symbol, 'percent')"
                                    @click="handleSort(skill, symbol, 'percent')"
                                >%</span>
                            </div>
                        </th>
                    </template>
                </tr>
            </thead>
            <tbody>
                <tr v-for="(player, index) in rows" :key="player.id" class="advanced-stats-table__row">
                    <td class="col-player sticky-col">
                        <div class="player-cell">
                            <span class="player-rank" aria-hidden="true">{{ index + 1 }}</span>
                            <Link :href="player.url" class="player-link">
                                <RegisteredBadge :status="player.status" size="sm" />
                                {{ player.name }}
                            </Link>
                        </div>
                    </td>
                    <td class="col-played col-played--matches sticky-col sticky-col--matches">
                        {{ player.matches_played ?? 0 }}
                    </td>
                    <td class="col-played col-played--sets sticky-col sticky-col--sets">
                        {{ player.sets_played ?? 0 }}
                    </td>
                    <template v-for="skill in skills" :key="`${player.id}-${skill}`">
                        <td class="col-metric col-total">{{ displayTotal(player, skill) }}</td>
                        <td class="col-metric col-eff">{{ displayEfficiency(player, skill) }}</td>
                        <td
                            v-for="symbol in qualitySymbols"
                            :key="`${player.id}-${skill}-${symbol}`"
                            class="col-metric col-value"
                            :class="qualityClass(symbol)"
                        >
                            <div class="quality-cell">
                                <span class="quality-cell__value">{{ displayQuality(player, skill, symbol) }}</span>
                                <span v-if="hasSkillData(player, skill)" class="quality-cell__pct">
                                    {{ displayQualityPercent(player, skill, symbol) }}
                                </span>
                            </div>
                        </td>
                    </template>
                </tr>
            </tbody>
        </table>

        <p v-else class="advanced-stats-table__empty">Нет данных за выбранный период</p>
    </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { Link } from '@inertiajs/vue3';
import RegisteredBadge from '@/Components/shared/RegisteredBadge.vue';
import {
    SKILLS,
    QUALITY_SYMBOLS,
    SKILL_LABELS,
    QUALITY_TITLES,
} from '@/utils/skillQualityConfig';

const props = defineProps({
    players: { type: Array, default: () => [] },
});

const skills = SKILLS;
const qualitySymbols = QUALITY_SYMBOLS;
const skillLabels = SKILL_LABELS;
const qualityTitles = QUALITY_TITLES;
const columnsPerSkill = 2 + qualitySymbols.length;

const sortState = ref({
    skill: null,
    field: null,
    mode: 'count',
    direction: 'desc',
});

const rows = computed(() => {
    const list = [...props.players];

    if (!sortState.value.field) {
        return list;
    }

    const { skill, field, mode, direction } = sortState.value;
    const multiplier = direction === 'asc' ? 1 : -1;

    list.sort((a, b) => {
        const aValue = sortValue(a, skill, field, mode);
        const bValue = sortValue(b, skill, field, mode);

        if (aValue === bValue) {
            return a.name.localeCompare(b.name, 'ru');
        }

        return (aValue - bValue) * multiplier;
    });

    return list;
});

function sortValue(player, skill, field, mode = 'count') {
    if (field === 'matches_played') return player.matches_played || 0;
    if (field === 'sets_played') return player.sets_played || 0;

    const skillData = player.skills?.[skill];
    if (!skillData) return 0;

    if (field === 'total') return skillData.total || 0;
    if (field === 'efficiency') return skillData.efficiency || 0;

    const count = skillData.qualities?.[field] || 0;
    if (mode === 'percent') {
        const total = skillData.total || 0;
        if (total <= 0) return 0;
        return (count / total) * 100;
    }

    return count;
}

function handleSort(skill, field, mode = 'count') {
    if (
        sortState.value.skill === skill
        && sortState.value.field === field
        && sortState.value.mode === mode
    ) {
        sortState.value.direction = sortState.value.direction === 'desc' ? 'asc' : 'desc';
        return;
    }

    sortState.value = {
        skill,
        field,
        mode,
        direction: 'desc',
    };
}

function sortClass(skill, field, mode = 'count') {
    if (
        sortState.value.skill !== skill
        || sortState.value.field !== field
        || sortState.value.mode !== mode
    ) {
        return '';
    }

    return sortState.value.direction === 'desc' ? 'sorted-desc' : 'sorted-asc';
}

function displayTotal(player, skill) {
    const total = player.skills?.[skill]?.total || 0;
    return total > 0 ? total : '—';
}

function displayEfficiency(player, skill) {
    const total = player.skills?.[skill]?.total || 0;
    if (total <= 0) return '—';
    return `${player.skills[skill].efficiency}%`;
}

function hasSkillData(player, skill) {
    return (player.skills?.[skill]?.total || 0) > 0;
}

function displayQuality(player, skill, symbol) {
    const total = player.skills?.[skill]?.total || 0;
    if (total <= 0) return '—';
    const value = player.skills?.[skill]?.qualities?.[symbol] || 0;
    return value > 0 ? value : '0';
}

function displayQualityPercent(player, skill, symbol) {
    const total = player.skills?.[skill]?.total || 0;
    if (total <= 0) return '';
    const value = player.skills?.[skill]?.qualities?.[symbol] || 0;
    return `${((value / total) * 100).toFixed(1)}%`;
}

function qualityClass(symbol) {
    if (symbol === '#') return 'is-hash';
    if (symbol === '=') return 'is-error';
    return '';
}
</script>
